/**
 * Recruiter reminders — the client half of /api/ta-action-board/reminders.
 *
 * Reminders are always the SIGNED-IN user's own: the proxy scopes every route to the verified
 * Firebase identity, so there is nothing to pass and no way to read a colleague's. No Gmail token
 * either — reminders touch no mailbox.
 *
 * PII note for callers: `title` and `candidateName` usually name a candidate. They belong in
 * Firestore (6-month retention, server-side) and on screen — NEVER in analytics events.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/ta-action-board/reminders`;

export interface TaReminder {
  id: string;
  title: string;
  /** ISO deadline — the strip sorts by it and flags overdue. */
  dueAt: string;
  /** The request card it was created from, for the Gmail link. Null for free-standing reminders. */
  threadId: string | null;
  /** Set on a follow-up created by the Outreach tab — links back to that candidate's card. */
  outreachId?: string | null;
  candidateName: string | null;
  status: 'open' | 'done';
  createdAt: string;
  completedAt: string | null;
}

async function readError(res: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) message = body.error;
  } catch {
    // Non-JSON error body — keep the fallback.
  }
  throw new Error(message);
}

export async function fetchReminders(): Promise<TaReminder[]> {
  const res = await authenticatedFetch(BASE);
  if (!res.ok) await readError(res, 'Could not load your reminders');
  const data = (await res.json()) as { items: TaReminder[] };
  return data.items;
}

export async function createReminder(input: {
  title: string;
  dueAt: string;
  threadId?: string | null;
  candidateName?: string | null;
}): Promise<TaReminder> {
  const res = await authenticatedFetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) await readError(res, 'Could not save the reminder');
  const data = (await res.json()) as { reminder: TaReminder };
  return data.reminder;
}

export async function updateReminder(
  id: string,
  patch: { title?: string; dueAt?: string; status?: 'open' | 'done' }
): Promise<TaReminder> {
  const res = await authenticatedFetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) await readError(res, 'Could not save the reminder');
  const data = (await res.json()) as { reminder: TaReminder };
  return data.reminder;
}

export async function deleteReminder(id: string): Promise<void> {
  const res = await authenticatedFetch(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Could not delete the reminder');
}
